import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { JSend } from '../../models/jsend.model';
import { StopCookiesStatus, StopNotifiedUser, StopRefreshResult } from '../models/stop-cookies.model';

/**
 * Estado del proceso de renovación de las cookies de SSTA y disparo manual.
 *
 * El backend es el servicio agrology-stop-cookie-refresher, expuesto en el
 * dominio agrology (no en api2): tiene su propio placeholder
 * `/:apiStopCookies/` en api-conf.ts y su URL por ambiente en environment.*.ts.
 */
@Injectable({
  providedIn: 'root'
})
export class StopCookiesService {

  private baseUrl = '/:apiStopCookies/companies/:companyId/ssta-cookies';

  constructor(
    private http: HttpClient
  ) { }

  /** Estado completo: cookies, cron, última corrida, cuota y fuente efectiva. */
  public getStatus(companyId: number): Observable<StopCookiesStatus> {
    const url = `${this.baseUrl.replace(':companyId', companyId.toString())}/status`;

    // OJO con el shape: el backend devuelve JSend `{status:"success", data:{...}}`
    // donde `data` ES el estado (cookies, cron, lastRun...), no un objeto que lo
    // contenga. Leer `response.data.status` daba undefined — y el `status` de
    // JSend ("success") no tiene nada que ver con el estado de las cookies.
    return this.http.get<JSend<StopCookiesStatus>>(url)
      .pipe(map(response => plainToInstance(StopCookiesStatus, response.data)));
  }

  /**
   * Dispara una renovación manual.
   *
   * ASINCRÓNICO POR NECESIDAD, no por elegancia: resolver el captcha tarda
   * 32-86s y el API Gateway HTTP corta a los 30s duros. El backend responde 202
   * de inmediato y el componente poleá getStatus() hasta que `refreshJob` deje
   * de estar en 'running'.
   *
   * Si la cookie ya está vigente responde 200 con outcome 'already_valid' y NO
   * consume cuota: el objetivo es sostener el régimen de un solo login por día.
   *
   * Errores esperados: 429 (tope diario global), 409 (ya hay una renovación en
   * curso).
   */
  public refresh(companyId: number): Observable<StopRefreshResult> {
    const url = `${this.baseUrl.replace(':companyId', companyId.toString())}/refresh`;

    // Devuelve el DESENLACE del disparo, no el estado completo: el componente
    // tiene que recargar el GET /status aparte. Pisar `this.status` con esto
    // dejaría la tabla de cookies vacía.
    return this.http.post<JSend<StopRefreshResult>>(url, {})
      .pipe(map(response => plainToInstance(StopRefreshResult, response.data)));
  }

  /** Usuarios que reciben el aviso si la renovación falla. Por company. */
  public getNotifiedUsers(companyId: number): Observable<StopNotifiedUser[]> {
    const url = `${this.baseUrl.replace(':companyId', companyId.toString())}/notified-users`;

    return this.http.get<JSend<{ users: StopNotifiedUser[] }>>(url)
      .pipe(map(response => (response.data.users || [])
        .map(user => plainToInstance(StopNotifiedUser, user))));
  }

  /**
   * Guarda la lista completa (reemplazo idempotente), no altas y bajas sueltas:
   * la UI es un multi-select que se guarda entero.
   */
  public saveNotifiedUsers(companyId: number, users: StopNotifiedUser[]): Observable<StopNotifiedUser[]> {
    const url = `${this.baseUrl.replace(':companyId', companyId.toString())}/notified-users`;

    return this.http.put<JSend<{ users: StopNotifiedUser[] }>>(url, { users })
      .pipe(map(response => (response.data.users || [])
        .map(user => plainToInstance(StopNotifiedUser, user))));
  }
}
